import type { DrawerConfig } from '@/features/drawer/types/DrawerConfig';
import type { PropsWithChildren } from 'react';
import { forwardRef, memo, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Typography from '@mui/material/Typography';
import { BaseDrawerBodyWrapper, BaseDrawerStyled } from '@/features/drawer/components/BaseDrawer/BaseDrawer.styles';
import { DRAWER_INDEX } from '@/features/drawer/constants/drawerIndex';
import { DRAWER_ANIMATION_MS } from '@/features/drawer/constants/drawerTimeout';
import { DrawerAnchor } from '@/features/drawer/enums/DrawerAnchor';
import { DrawerStatus } from '@/features/drawer/enums/DrawerStatus';
import { Drawers } from '@/features/drawer/enums/Drawers';
import ErrorBoundary from '@/components/helpers/ErrorBoundary/ErrorBoundary';
import { useHeaderHeight } from '@/hooks/useHeaderHeight';

export type BaseDrawerProps = PropsWithChildren<{
  drawerKey: Drawers;
  configuration: DrawerConfig;
}>;

export const BaseDrawer = memo(
  forwardRef<HTMLDivElement, BaseDrawerProps>(({ drawerKey, configuration, children }, ref) => {
    const [status, setStatus] = useState<DrawerStatus>(DrawerStatus.Hidden);

    const { anchor, size, styles, offsets, behavior } = configuration;
    const headerHeight = useHeaderHeight();

    const offsetTop = useMemo(
      () => (behavior?.isHeaderOffsetEnabled ? headerHeight : 0),
      [headerHeight, behavior?.isHeaderOffsetEnabled],
    );
    const offsetRight = useMemo(() => offsets?.right || 0, [offsets?.right]);

    useEffect(() => {
      setTimeout(() => setStatus(DrawerStatus.Showed), DRAWER_ANIMATION_MS);
    }, []);

    return createPortal(
      <BaseDrawerStyled
        key={`drawer-${drawerKey}`}
        open
        hideBackdrop
        data-testid={`BaseDrawer-${drawerKey}`}
        variant="persistent"
        anchor={anchor || DrawerAnchor.Right}
        offsetRight={offsetRight}
        status={status}
        size={size}
        zIndex={styles?.common?.zIndex || DRAWER_INDEX}
        data-is-drawer>
        <BaseDrawerBodyWrapper ref={ref} size={size} offsetTop={offsetTop} background={styles?.common?.background}>
          <ErrorBoundary
            showError
            fallback={
              <Typography variant="body2">
                It looks like something went wrong while loading this section of the page.
              </Typography>
            }>
            {children}
          </ErrorBoundary>
        </BaseDrawerBodyWrapper>
      </BaseDrawerStyled>,
      document.getElementById('drawer-root') as HTMLElement,
    );
  }),
  (prevProps, nextProps) => {
    return prevProps.configuration === nextProps.configuration && prevProps.drawerKey === nextProps.drawerKey;
  },
);

BaseDrawer.displayName = 'BaseDrawer';
